/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import {
    Affix,
    Alert,
    Button,
    Form,
    Icon,
    Input,
    message,
    Modal,
    Row,
    Table,
    Tabs,
    Tooltip,
} from 'antd';
import { FormComponentProps } from 'antd/es/form';
import { errorHandle } from '../../services';
import {
    DEFAULT_PAGE_SIZE,
    LIST_TYPE,
    PAGE_SIZE_OPTIONS,
    STICKY_ALERT_OFFSET_TOP,
} from './constants';
import {
    addWhitelist,
    assertBizSuccess,
    deleteWhitelist,
    fetchWhitelistPage,
    mapWhitelistItem,
    pickAddMessage,
    pickPageResult,
} from './api';
import { ProductSelectItem, ProductSelectModal } from './product-select-modal';
import { BanOnImportModal } from './ban-on-import-modal';
import { BanOnAddModal } from './ban-on-add-modal';
import { formatDateTime, getBanOnDeleteContent } from './ban-on-utils';

const { TabPane } = Tabs;

type DimType = 'SPU' | 'SKU';

const cleanParams = (params: Record<string, any>) => {
    const next = { ...params };
    Object.keys(next).forEach((key) => {
        if (next[key] === undefined || next[key] === null || next[key] === '') {
            delete next[key];
        }
    });
    return next;
};

const renderEllipsisCell = (text?: string) => {
    const value = text != null && String(text).trim() !== '' ? String(text).trim() : '-';
    if (value === '-') {
        return value;
    }
    return (
        <Tooltip title={value} placement="topLeft">
            <span className="shelf-rule__cell-ellipsis">{value}</span>
        </Tooltip>
    );
};

const BanOffListTabCom = ({ form }: FormComponentProps) => {
    const [dim, setDim] = useState<DimType>('SPU');
    const [pageNum, setPageNum] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [searchParams, setSearchParams] = useState<Record<string, any>>({});
    const [listData, setListData] = useState<any[]>([]);
    const [addVisible, setAddVisible] = useState(false);
    const [selectVisible, setSelectVisible] = useState(false);
    const [selectedItems, setSelectedItems] = useState<ProductSelectItem[]>([]);
    const [remark, setRemark] = useState('');
    const [addErrors, setAddErrors] = useState({ code: '', remark: '' });
    const [addSubmitting, setAddSubmitting] = useState(false);
    const [importVisible, setImportVisible] = useState(false);
    const [deleteRecord, setDeleteRecord] = useState<any>(null);
    const [deleteSubmitting, setDeleteSubmitting] = useState(false);

    const fetchList = (query: Record<string, any> = {}) => {
        const payload = cleanParams({
            listType: LIST_TYPE.UNLIST_BAN,
            dimension: dim,
            page: pageNum,
            per_page: pageSize,
            ...searchParams,
            ...query,
        });
        setLoading(true);
        fetchWhitelistPage(payload)
            .then((res: any) => {
                const { list, total: nextTotal } = pickPageResult(res);
                setListData(list.map(mapWhitelistItem));
                setTotal(nextTotal);
                setPageNum(payload.page || 1);
                setPageSize(payload.per_page || pageSize);
            })
            .catch(errorHandle)
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchList({ page: 1, per_page: pageSize });
    }, []);

    const handleSearch = (e?: React.FormEvent) => {
        if (e) {
            e.preventDefault();
        }
        form.validateFields((_err: any, values: any) => {
            const params = {
                code: values.code ? values.code.trim() : undefined,
                name: values.name ? values.name.trim() : undefined,
            };
            setSearchParams(params);
            fetchList({ ...params, page: 1, per_page: pageSize });
        });
    };

    const handleReset = () => {
        form.resetFields();
        setSearchParams({});
        fetchList({ page: 1, per_page: pageSize, code: undefined, name: undefined });
    };

    const handleDelete = (record: any) => {
        setDeleteRecord(record);
    };

    const closeDelete = () => {
        if (deleteSubmitting) {
            return;
        }
        setDeleteRecord(null);
    };

    const confirmDelete = () => {
        if (!deleteRecord || !deleteRecord.id) {
            message.error('缺少名单记录，无法删除');
            return;
        }
        setDeleteSubmitting(true);
        deleteWhitelist(deleteRecord.id)
            .then((res: any) => {
                if (!assertBizSuccess(res)) {
                    message.error(pickAddMessage(res) || '删除失败');
                    return;
                }
                const resultMsg = pickAddMessage(res);
                if (resultMsg) {
                    message.success(resultMsg);
                }
                setDeleteRecord(null);
                const nextPage = listData.length === 1 && pageNum > 1 ? pageNum - 1 : pageNum;
                fetchList({ page: nextPage, per_page: pageSize });
            })
            .catch(errorHandle)
            .finally(() => {
                setDeleteSubmitting(false);
            });
    };

    const openImport = () => {
        setImportVisible(true);
    };

    const openAdd = () => {
        setSelectedItems([]);
        setRemark('');
        setAddErrors({ code: '', remark: '' });
        setAddVisible(true);
    };

    const closeAdd = () => {
        setAddVisible(false);
        setSelectVisible(false);
        setSelectedItems([]);
        setRemark('');
        setAddErrors({ code: '', remark: '' });
    };

    const handleSelectOk = (items: ProductSelectItem[]) => {
        setSelectedItems(items);
        setSelectVisible(false);
        if (items.length) {
            setAddErrors((prev) => ({ ...prev, code: '' }));
        }
    };

    const handleAddOk = () => {
        const nextErrors = {
            code: selectedItems.length ? '' : `请选择一个或多个 ${dim}`,
            remark: remark.trim() ? '' : '请填写说明',
        };
        setAddErrors(nextErrors);
        if (nextErrors.code || nextErrors.remark) {
            return;
        }
        setAddSubmitting(true);
        addWhitelist({
            list_type: LIST_TYPE.UNLIST_BAN,
            dimension: dim,
            object_codes: selectedItems.map((item) => item.code),
            remark: remark.trim(),
        })
            .then((res: any) => {
                if (!assertBizSuccess(res)) {
                    message.error(pickAddMessage(res) || '添加失败');
                    return;
                }
                const resultMsg = pickAddMessage(res);
                if (resultMsg.includes('成功添加')) {
                    message.success(resultMsg || '添加成功');
                    closeAdd();
                } else if (resultMsg) {
                    message.warning(resultMsg);
                } else {
                    message.success('添加成功');
                    closeAdd();
                }
                fetchList({ page: 1, per_page: pageSize });
            })
            .catch(errorHandle)
            .finally(() => {
                setAddSubmitting(false);
            });
    };

    const handleDimChange = (key: string) => {
        const nextDim = key as DimType;
        setDim(nextDim);
        setPageNum(1);
        form.resetFields();
        setSearchParams({});
        closeAdd();
        setDeleteRecord(null);
        setImportVisible(false);
        fetchList({
            dimension: nextDim,
            page: 1,
            per_page: pageSize,
            code: undefined,
            name: undefined,
        });
    };

    const columns: any[] = [
        {
            title: `${dim}编码`,
            dataIndex: 'code',
            key: 'code',
            width: 160,
            className: 'shelf-rule__ellipsis-col',
            render: (text: string) => renderEllipsisCell(text),
        },
        {
            title: '名称',
            dataIndex: 'name',
            key: 'name',
            width: 220,
            className: 'shelf-rule__ellipsis-col',
            render: (text: string) => renderEllipsisCell(text),
        },
        {
            title: '说明',
            dataIndex: 'remark',
            key: 'remark',
            width: 220,
            className: 'shelf-rule__ellipsis-col',
            render: (text: string) => renderEllipsisCell(text),
        },
        {
            title: '添加人',
            dataIndex: 'creator',
            key: 'creator',
            width: 160,
        },
        {
            title: '添加时间',
            dataIndex: 'createTime',
            key: 'createTime',
            width: 180,
            render: (text: string | number) => formatDateTime(text),
        },
        {
            title: '操作',
            key: 'action',
            width: 80,
            render: (_: any, record: any) => <a onClick={() => handleDelete(record)}>删除</a>,
        },
    ];

    return (
        <div>
            <Affix offsetTop={STICKY_ALERT_OFFSET_TOP}>
                <div className="shelf-rule__sticky-alert">
                    <Alert
                        type="warning"
                        showIcon
                        message="禁止下架名单说明"
                        description="所属 SPU 或该 SKU 任一在禁止下架名单时，不会自动下架；无货仍展示，前台可搜索。添加后长期生效；删除后立即对相关 SKU 重算规则。人工下架不会自动移出本名单；零库存人工上架时可一并加入。"
                    />
                </div>
            </Affix>
            <Tabs
                activeKey={dim}
                onChange={handleDimChange}
                animated={{ inkBar: true, tabPane: false }}
            >
                <TabPane tab="SPU" key="SPU" />
                <TabPane tab="SKU" key="SKU" />
            </Tabs>
            <Form layout="inline" onSubmit={handleSearch} className="shelf-rule__filter">
                <Form.Item label={`${dim}编码`}>
                    {form.getFieldDecorator('code')(
                        <Input placeholder={`${dim} 编码`} style={{ width: 180 }} />
                    )}
                </Form.Item>
                <Form.Item label="名称">
                    {form.getFieldDecorator('name')(
                        <Input placeholder="商品名称" style={{ width: 180 }} />
                    )}
                </Form.Item>
                <Form.Item>
                    <Button type="primary" htmlType="submit">
                        查询
                    </Button>
                    <Button style={{ marginLeft: 8 }} onClick={handleReset}>
                        重置
                    </Button>
                </Form.Item>
            </Form>
            <Row style={{ marginBottom: 16 }}>
                <Button type="primary" onClick={openAdd}>
                    添加
                </Button>
                <Button style={{ marginLeft: 8 }} onClick={openImport}>
                    批量导入
                </Button>
            </Row>
            <BanOnAddModal
                dim={dim}
                visible={addVisible}
                title="添加禁止下架名单"
                selectedItems={selectedItems}
                remark={remark}
                errors={addErrors}
                confirmLoading={addSubmitting}
                onCancel={closeAdd}
                onOk={handleAddOk}
                onOpenSelect={() => setSelectVisible(true)}
                onRemoveSelected={(code) =>
                    setSelectedItems((prev) => prev.filter((item) => item.code !== code))
                }
                onRemarkChange={(value) => {
                    setRemark(value);
                    if (value.trim()) {
                        setAddErrors((prev) => ({ ...prev, remark: '' }));
                    }
                }}
            />
            <ProductSelectModal
                key={`ban-off-${dim}`}
                visible={selectVisible}
                dim={dim}
                listType={LIST_TYPE.UNLIST_BAN}
                defaultSelected={selectedItems}
                onCancel={() => setSelectVisible(false)}
                onOk={handleSelectOk}
            />
            <BanOnImportModal
                visible={importVisible}
                dim={dim}
                listType={LIST_TYPE.UNLIST_BAN}
                onCancel={() => setImportVisible(false)}
            />
            <Modal
                title="删除确认"
                visible={!!deleteRecord}
                onCancel={closeDelete}
                onOk={confirmDelete}
                confirmLoading={deleteSubmitting}
                okText="确认删除"
                cancelText="取消"
                destroyOnClose
            >
                <div className="shelf-rule__delete-confirm">
                    <Icon
                        type="exclamation-circle"
                        theme="filled"
                        style={{ color: '#faad14', fontSize: 22 }}
                        className="shelf-rule__delete-confirm-icon"
                    />
                    <div className="shelf-rule__delete-confirm-text">
                        {deleteRecord ? getBanOnDeleteContent(dim, deleteRecord) : ''}
                    </div>
                </div>
            </Modal>
            <Table
                className="shelf-rule__table"
                rowKey="id"
                loading={loading}
                columns={columns}
                dataSource={listData}
                locale={{ emptyText: `暂无${dim}禁止下架名单` }}
                pagination={{
                    total,
                    current: pageNum,
                    pageSize,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    pageSizeOptions: PAGE_SIZE_OPTIONS,
                    showTotal: (count) => `共 ${count} 条记录`,
                    onChange: (page) => {
                        fetchList({ page, per_page: pageSize });
                    },
                    onShowSizeChange: (_current, size) => {
                        fetchList({ page: 1, per_page: size });
                    },
                }}
            />
        </div>
    );
};

export const BanOffListTab = Form.create()(BanOffListTabCom);
